import {Component, ViewChild} from '@angular/core';
import {ReplaySubject} from 'rxjs';
import {PersonalDetailsSectionService} from '../../model/personal-details-section.service';
import {CountryService} from '../../../../domain/model/info/country/country.service';
import {NgForm} from '@angular/forms';
import {FormErrors} from '../../../../components/forms/validation/form-validator.class';
import {StepScreenModel} from '../../../shared/step/step-screen.component';
import * as moment from 'moment';
import {FileLocationFactory} from '../../../../components/files/service/file-location.class';
import {Model} from '../../../../domain/model/common/model.class';
import {Option} from '../../../../components/forms/controls/selector/selector.component';
import {IdentificationInfoProjectionType} from '../../model/identification-info.model';
import {Profile} from '../../../../domain/model/profile/profile.model';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';
import {combineLatest} from 'rxjs/observable/combineLatest';
import {ProfileService} from '../../../../domain/model/profile/profile.service';
import {BackendError} from '../../../../common/error/backend-error.class';
import _ = require('lodash');
import ProjectionInformation = Model.ProjectionInformation;
import {VerificationService} from '../../../../components/verification/service/verification.service';

//region NPI Field Constants
const EXISTING_NPI_ERROR_MESSAGE = 'Another profile with the given NPI already exists.';
const NPI_CHANGE_DEBOUNCE_TIME_MS = 1000;
const NPI_REGEX = /^\d{10}$/;

//endregion

@Component({
    template: require('./identification-screen.component.html')
})
export class IdentificationScreenComponent {

    // region Form
    readonly form$ = new ReplaySubject<NgForm>(1);

    @ViewChild(NgForm)
    set form(value: NgForm) {
        this.form$.next(value);
    }

    // endregion

    //region NPI Field
    readonly npiMask = [/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/];

    readonly npiChange$ = new ReplaySubject<string>(1);
    private readonly _npiError$ = new BehaviorSubject<string | undefined>(undefined);
    //endregion

    countries: Array<string> = [];
    states: Array<string> = [];
    ssnMask = [/\d/, /\d/, /\d/, '-', /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/];
    birthDateConstrains = {
        min: moment().subtract(100, 'year'),
        max: moment().subtract(18, 'year')
    };
    expirationDateConstrains = {
        min: moment().subtract(5, 'year'),
        max: moment().add(20, 'year')
    };
    driverLicenseFileLocation = this.fileLocationFactory.create('profile', 'currentUser', new ProjectionInformation('driverLicenseFile'));
    workPermissionTypes = [
        new Option('US_CITIZEN', 'US Citizen'),
        new Option('RESIDENT', 'Resident'),
        new Option('VISA', 'Visa'),
        new Option('WORK_AUTHORIZATION', 'Work Authorization')];

    formErrors: FormErrors = {};
    error: string;

    stepScreenModel = new StepScreenModel<IdentificationInfoProjectionType>({
        initialDataModel: new Profile(),
        specificValidationMessages: {
            ssn: 'The SSN should be 9 digits long.',
            npi: 'The NPI should be 10 digits long.'
        },
        _loadDataFn: () => {
            this.verificationService.setUrl('section/personalDetails', 'section=identificationInfo');
            return this.personalDetailsService.currentUserIdentificationInfo();
        },
        _submitDataFn: (model: IdentificationInfoProjectionType) => {
            return this.personalDetailsService.updateCurrentUserIdentificationInfo(model);
        },
        _error: (error?: BackendError) => {
            let partialError: Partial<BackendError> = error || {};
            switch (partialError.name) {
                case 'ExistingNpiError':
                    this.error = EXISTING_NPI_ERROR_MESSAGE;
                    break;
                default:
                    this.error = 'We are sorry, a server error just occurred.';
            }
        }
    });

    get model(): IdentificationInfoProjectionType {
        return this.stepScreenModel.dataModel;
    }

    constructor(readonly personalDetailsService: PersonalDetailsSectionService,
                readonly countryService: CountryService,
                private readonly _profileService: ProfileService,
                readonly fileLocationFactory: FileLocationFactory,
                readonly verificationService: VerificationService) {
        countryService.getCountriesProjection().subscribe(countries => this.countries = countries.map(c => c.name));
        countryService.getStates().subscribe(states => this.states = states.map(s => s.name));

        combineLatest(this.stepScreenModel.formErrors$, this._npiError$,
            (errors, npiError) => {
                if (npiError) return _.merge({}, errors, {npi: npiError});
                else return errors;
            })
            .subscribe(errors => this.formErrors = errors);

        this._initNpiFields();
    }

    //region NPI Field
    private _initNpiFields() {
        this.npiChange$.debounceTime(NPI_CHANGE_DEBOUNCE_TIME_MS)
            .distinctUntilChanged()
            .switchMap(value => {
                if (NPI_REGEX.test(value)) return this._profileService.doesAnotherProfileWithSameNpiExist(value);
                else return Observable.of(false);
            })
            .subscribe(exists => {
                let error: string | undefined;
                if (exists) {
                    error = EXISTING_NPI_ERROR_MESSAGE;
                }
                this._npiError$.next(error);
            });
    }

    //endregion
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/pages/personal-details/steps/identification/identification-screen.component.ts